import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Trash2 } from 'lucide-react';
import SearchBar from './SearchBar';
import SortControls from './SortControls';
import { makeAuthenticatedRequest } from '../utils/api';
import { API_BASE_URL } from '../config';

export default function ApplicantsList({
  applicants,
  selectedJob,
  appSearch,
  setAppSearch,
  appSortBy,
  setAppSortBy,
  appSortOrder,
  setAppSortOrder,
  appTotalCount,
  appPage,
  appTotalPages,
  handleApplicantsSearch,
  handleApplicantsClearSearch,
  fetchApplicantsWithParams,
  evaluateMatch
}) {
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [matchResults, setMatchResults] = useState(null);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [showResumeExtract, setShowResumeExtract] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletingApplicant, setDeletingApplicant] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteApplicant = async (applicant) => {
    setDeleteLoading(true);
    
    try {
      const response = await makeAuthenticatedRequest(
        `${API_BASE_URL}/applicants/application/${applicant.application_id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete applicant');
      }

      // Close the confirmation modal
      setShowDeleteConfirmation(false);
      setDeletingApplicant(null);
      
      // Refresh the applicants list
      fetchApplicantsWithParams(appPage);
      
      // Show success message (you might want to implement a toast notification system)
      console.log('Applicant deleted successfully');
      
    } catch (error) {
      console.error('Error deleting applicant:', error);
      // You might want to show an error message to the user
      alert(`Error: ${error.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmDeleteApplicant = (applicant) => {
    setDeletingApplicant(applicant);
    setShowDeleteConfirmation(true);
  };

  return (
    <div className="p-4 flex-grow-1">
      {/* Search, Sort, and Pagination Controls */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <SearchBar
          searchQuery={appSearch}
          setSearchQuery={setAppSearch}
          handleSearch={handleApplicantsSearch}
          handleClear={handleApplicantsClearSearch}
          totalCount={appTotalCount}
          displayCount={applicants.length}
        />
        <SortControls
          sortBy={appSortBy}
          setSortBy={setAppSortBy}
          sortOrder={appSortOrder}
          setSortOrder={setAppSortOrder}
          onSortChange={fetchApplicantsWithParams}
        />
      </div>

      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>Name</th>
              <th>Match Score</th>
              <th>Email</th>
              <th>Location</th>
              <th>Experience</th>
              <th>Education</th>
              <th>Skills</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applicants.length > 0 ? (
              applicants.map(applicant => (
                <tr key={applicant.application_id}>
                  <td>
                    <Link 
                      to={`/candidate/${applicant.candidate.id}`}
                      className="text-decoration-none text-primary"
                      state={{ 
                        jobId: selectedJob.job_id,
                        jobTitle: selectedJob.title,
                        applicationId: applicant.application_id,
                        applicationStatus: applicant.status,
                        from: 'jobDetails'
                      }}
                    >
                      {`${applicant.candidate.first_name} ${applicant.candidate.last_name}`}
                    </Link>
                  </td>
                  <td>
                    {applicant.match_score ? (
                      <span className={`badge ${
                        applicant.match_score >= 7 ? 'bg-success' :
                        applicant.match_score >= 4 ? 'bg-warning' :
                        'bg-danger'
                      }`}>
                        {applicant.match_score}/10
                      </span>
                    ) : '-'}
                  </td>
                  <td>{applicant.candidate.email}</td>
                  <td>{applicant.candidate.location}</td>
                  <td>{applicant.candidate.experience_years} years</td>
                  <td>{applicant.candidate.education}</td>
                  <td>{applicant.candidate.skills ? applicant.candidate.skills.join(", ") : "Not specified"}</td>
                  <td>
                    <span className={`badge ${
                      applicant.status === 'applied' ? 'bg-primary' :
                      applicant.status === 'interviewing' ? 'bg-warning' :
                      applicant.status === 'offered' ? 'bg-success' :
                      applicant.status === 'rejected' ? 'bg-danger' :
                      'bg-secondary'
                    }`}>
                      {applicant.status.charAt(0).toUpperCase() + applicant.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div className="dropdown">
                      <button 
                        className="btn btn-outline-primary btn-sm me-1 dropdown-toggle"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button>
                      <ul className="dropdown-menu">
                        <li>
                          <button 
                            className="dropdown-item"
                            onClick={() => {
                              setSelectedApplicant(applicant);
                              evaluateMatch(applicant.candidate.id, selectedJob.job_id);
                            }}
                            disabled={loadingMatch}
                          >
                            Show Match
                          </button>
                        </li>
                        {applicant.candidate.resume_extract && (
                          <li>
                            <button 
                              className="dropdown-item"
                              onClick={() => {
                                setSelectedApplicant(applicant);
                                setShowResumeExtract(true);
                              }}
                            >
                              Show Resume Extract
                            </button>
                          </li>
                        )}
                        <li><hr className="dropdown-divider" /></li>
                        <li>
                          <button 
                            className="dropdown-item text-danger"
                            onClick={() => confirmDeleteApplicant(applicant)}
                            disabled={deleteLoading}
                          >
                            <Trash2 size={14} className="me-2" />
                            Delete Applicant
                          </button>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  No applicants found for this job posting
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <nav>
          <ul className="pagination justify-content-center mt-3">
            <li className={`page-item ${appPage <= 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => fetchApplicantsWithParams(appPage - 1)}>Previous</button>
            </li>
            {Array.from({ length: appTotalPages }, (_, i) => (
              <li key={i} className={`page-item ${appPage === i + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => fetchApplicantsWithParams(i + 1)}>{i + 1}</button>
              </li>
            ))}
            <li className={`page-item ${appPage >= appTotalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => fetchApplicantsWithParams(appPage + 1)}>Next</button>
            </li>
          </ul>
        </nav>
      </div>

      {/* Loading Modal */}
      {loadingMatch && selectedApplicant && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="mb-3">Evaluating Match</h5>
                <p className="text-muted mb-0">
                  Our AI is analyzing the candidate's profile against the job requirements...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Match Results Modal */}
      {matchResults && selectedApplicant && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Match Evaluation Results</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setMatchResults(null);
                    setSelectedApplicant(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <h6 className="mb-2">Match Score</h6>
                  <div className="progress" style={{ height: '25px' }}>
                    <div 
                      className={`progress-bar ${
                        matchResults.matching_score >= 7 ? 'bg-success' :
                        matchResults.matching_score >= 4 ? 'bg-warning' :
                        'bg-danger'
                      }`}
                      role="progressbar" 
                      style={{ width: `${matchResults.matching_score * 10}%` }}
                    >
                      {matchResults.matching_score}/10
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="text-success mb-2">Matching Qualities</h6>
                  <p className="mb-0">{matchResults.matching_text}</p>
                </div>

                <div>
                  <h6 className="text-danger mb-2">Areas of Mismatch</h6>
                  <p className="mb-0">{matchResults.non_matching_text}</p>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setMatchResults(null);
                    setSelectedApplicant(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resume Extract Modal */}
      {showResumeExtract && selectedApplicant && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Resume Extract - {selectedApplicant.candidate.first_name} {selectedApplicant.candidate.last_name}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowResumeExtract(false);
                    setSelectedApplicant(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {selectedApplicant.candidate.resume_extract ? (
                  <div className="resume-extract" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedApplicant.candidate.resume_extract}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">No resume extract available</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowResumeExtract(false);
                    setSelectedApplicant(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && deletingApplicant && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setDeletingApplicant(null);
                  }}
                  disabled={deleteLoading}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  Are you sure you want to delete the application from{' '}
                  <strong>
                    {deletingApplicant.candidate.first_name} {deletingApplicant.candidate.last_name}
                  </strong>{' '}
                  for this job posting?
                </p>
                <div className="alert alert-warning">
                  <small>
                    <strong>Warning:</strong> This action cannot be undone. The applicant's profile will remain in the system, 
                    but their application for this specific job will be permanently removed.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setDeletingApplicant(null);
                  }}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={() => handleDeleteApplicant(deletingApplicant)}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} className="me-2" />
                      Delete Application
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 